import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import useUserStore from '@/store/userStore';

/**
 * 资源账户数据结构
 */
export interface ResourceAccount {
  userId: number;
  userTier: 'free' | 'paid' | 'guest';
  subscriptionTier: string;

  // 月度配额
  monthlyQuota: {
    total: number;
    used: number;
    remaining: number;
    resetDate: string;
  };

  // 积分余额
  points: {
    total: number;
    available: number;
    frozen: number;
  };

  // 每日免费配额
  dailyFree: {
    total: number;
    used: number;
    remaining: number;
    resetAt: string;
  };

  // 统计信息
  stats?: {
    totalTransactions: number;
    totalConsumed: number;
    totalRecharged: number;
  };
}

/**
 * 资源账户Store状态
 */
interface ResourceAccountState {
  // 资源账户数据
  account: ResourceAccount | null;

  // 加载状态
  isLoading: boolean;

  // 错误信息
  error: string | null;

  // 最后更新时间
  lastUpdated: number | null;

  // Actions
  actions: {
    setAccount: (account: ResourceAccount) => void;
    updateAccount: (updates: Partial<ResourceAccount>) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    clearAccount: () => void;

    // 刷新账户数据
    refreshAccount: () => Promise<void>;

    // 检查数据是否需要刷新（例如：超过5分钟）
    shouldRefresh: () => boolean;
  };
}

/**
 * 资源账户全局Store
 * 
 * 用于管理用户的资源账户信息，避免多次调用API
 * 
 * @example
 * ```tsx
 * // 在组件中使用
 * const { account, isLoading } = useResourceAccount();
 * 
 * // 获取特定数据
 * const monthlyRemaining = useResourceAccountStore(state => state.account?.monthlyQuota.remaining);
 * ```
 */
export const useResourceAccountStore = create<ResourceAccountState>()(
  devtools(
    (set, get) => ({
      account: null,
      isLoading: false,
      error: null,
      lastUpdated: null,

      actions: {
        setAccount: (account) => {
          set({
            account,
            lastUpdated: Date.now(),
            error: null
          });
        },

        updateAccount: (updates) => {
          const currentAccount = get().account;
          if (currentAccount) {
            set({
              account: { ...currentAccount, ...updates },
              lastUpdated: Date.now()
            });
          }
        },

        setLoading: (loading) => {
          set({ isLoading: loading });
        },

        setError: (error) => {
          set({ error, isLoading: false });
        },

        clearAccount: () => {
          set({
            account: null,
            error: null,
            lastUpdated: null,
            isLoading: false
          });
        },

        refreshAccount: async () => {
          const { setLoading, setError, setAccount } = get().actions;

          try {
            setLoading(true);
            setError(null);

            // 获取当前用户ID
            const userInfo = useUserStore.getState().userInfo;

            if (!userInfo?.id) {
              throw new Error('用户未登录');
            }

            // 调用API获取资源账户信息
            // 注意：这里需要添加获取当前用户资源账户的API
            const response = await fetch(`${import.meta.env.VITE_APP_API_BASE_URL || 'https://test-api.gpthumanizer.ai/api/v1'}/resource/me/account`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                // Token会由axios interceptor自动添加
              },
            });

            if (!response.ok) {
              throw new Error('获取资源账户信息失败');
            }

            const data = await response.json();

            if (data.code === 200 && data.data) {
              setAccount(data.data);
            } else {
              throw new Error(data.message || '获取资源账户信息失败');
            }
          } catch (error) {
            console.error('刷新资源账户失败:', error);
            setError(error instanceof Error ? error.message : '未知错误');
          } finally {
            setLoading(false);
          }
        },

        shouldRefresh: () => {
          const { lastUpdated } = get();

          // 如果从未更新过，应该刷新
          if (!lastUpdated) {
            return true;
          }

          // 如果超过5分钟，应该刷新
          const FIVE_MINUTES = 5 * 60 * 1000;
          return Date.now() - lastUpdated > FIVE_MINUTES;
        },
      },
    }),
    { name: 'ResourceAccountStore' }
  )
);

// ============= Selector Hooks =============

/**
 * 获取完整的资源账户信息
 */
export const useResourceAccount = () => {
  return useResourceAccountStore((state) => ({
    account: state.account,
    isLoading: state.isLoading,
    error: state.error,
    lastUpdated: state.lastUpdated,
  }));
};

/**
 * 获取资源账户的Actions
 */
export const useResourceAccountActions = () => {
  return useResourceAccountStore((state) => state.actions);
};

/**
 * 获取月度配额信息
 */
export const useMonthlyQuota = () => {
  return useResourceAccountStore((state) => state.account?.monthlyQuota);
};

/**
 * 获取积分余额信息
 */
export const usePointsBalance = () => {
  return useResourceAccountStore((state) => state.account?.points);
};

/**
 * 获取每日免费配额信息
 */
export const useDailyFreeQuota = () => {
  return useResourceAccountStore((state) => state.account?.dailyFree);
};

/**
 * 获取用户层级
 */
export const useUserTier = () => {
  return useResourceAccountStore((state) => state.account?.userTier);
};

export default useResourceAccountStore;

